import {
  filters,
  API_INFINITY_VALUE,
  FILTER_TYPE,
} from '../../../../../constants';
import type { CustomCompanyFilter } from './types';
import { dots } from '../../../../../functions';
import { toSignificant } from '@/utilities/formatting';

export const generateHealthFilters = (): CustomCompanyFilter[] => [
  // Debt to Equity
  {
    id: filters.debtEquity,
    label: 'Debt to Equity Ratio',
    labels: {
      ...dots(200, { step: 20 }),
      0: '0%',
      200: '200%',
      210: '∞',
    },
    softMax: 200,
    min: 0,
    max: 210,
    type: FILTER_TYPE.RANGE,
    defaultValue: `["aor",[["${filters.debtEquity}", "between", 0, 200],["${filters.debtEquity}", "is_null"]]]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        if (Number(min) === 0) {
          return `["aor",[["${filters.debtEquity}", "between", ${min}, ${max}],["${filters.debtEquity}", "is_null"]]]`;
        }
        return `["${filters.debtEquity}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: (rule) => {
      if (rule[0] === 'aor') {
        const nestedRule = rule[1][0];
        return [nestedRule[2], nestedRule[3]];
      }
      return [rule[2], rule[3]];
    },
    valueResolver: (range) =>
      range.map((value) =>
        value > 200 ? API_INFINITY_VALUE : toSignificant(value / 100, 2)
      ),
    titleFormatter: ([_, __, min, max]) => {
      return `is between ${min}% and ${
        max === API_INFINITY_VALUE ? '∞' : `${max}%`
      }`;
    },
  },
  // Net Debt to Equity
  {
    id: filters.net_debt_to_equity,
    label: 'Net Debt to Equity Ratio',
    subLabel: '(Total debt less cash to shareholder equity)',
    labels: {
      ...dots(200, { step: 20 }),
      0: '0%',
      200: '200%',
      210: '∞',
    },
    softMax: 200,
    min: 0,
    max: 210,
    type: FILTER_TYPE.RANGE,
    defaultValue: `["aor",[["${filters.net_debt_to_equity}", "between", 0, 50],["${filters.net_debt_to_equity}", "is_null"]]]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        if (Number(min) === 0) {
          return `["aor",[["${filters.net_debt_to_equity}", "between", ${min}, ${max}],["${filters.net_debt_to_equity}", "is_null"]]]`;
        }
        return `["${filters.net_debt_to_equity}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: (rule) => {
      if (rule[0] === 'aor') {
        const nestedRule = rule[1][0];
        return [nestedRule[2], nestedRule[3]];
      }
      return [rule[2], rule[3]];
    },
    valueResolver: (range) =>
      range.map((value) =>
        value > 200 ? API_INFINITY_VALUE : toSignificant(value / 100, 2)
      ),
    titleFormatter: ([_, __, min, max]) => {
      return `is between ${min}% and ${
        max === API_INFINITY_VALUE ? '∞' : `${max}%`
      }`;
    },
  },
  // Current Ratio
  {
    id: filters.health_current_solvency_ratio,
    label: 'Current Ratio',
    subLabel: '(Current Assets to Current Liabilities Ratio)',
    labels: {
      ...dots(100, { start: -50, step: 25 }),
      '-55': '-∞',
      '-50': '-50%',
      0: '0%',
      50: '50%',
      55: '∞',
    },
    defaultValue: `["${filters.health_current_solvency_ratio}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.health_current_solvency_ratio}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -55;
        } else if (value >= API_INFINITY_VALUE) {
          return 55;
        } else {
          return value;
        }
      }),
    min: -55,
    max: 55,
    type: FILTER_TYPE.RANGE,
    softMin: -50,
    softMax: 50,
    valueResolver: (range) =>
      range.map((value) =>
        value < -50
          ? API_INFINITY_VALUE * -1
          : value > 50
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
];
